import React, { useState } from 'react';
import { Layout, Menu } from 'antd';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SidebarData } from './SidebarData';
import { useAuthStore } from '../../store/authUserStore';
import { LogoutOutlined } from '@ant-design/icons';
import './SidebarComponent.css';
import { useTranslation } from 'react-i18next';

const { Sider } = Layout;

interface SidebarComponentProps {
  collapsed: boolean;
  toggleSidebar: () => void;
}

const SidebarComponent: React.FC<SidebarComponentProps> = ({ collapsed, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, currentUser, clearToken } = useAuthStore((state) => state);
  const { t } = useTranslation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const handleOpenChange = (keys: string[]) => {
    if (
      openKeys.length === 1 &&
      keys.length === 1 &&
      openKeys[0] === keys[0]
    ) {
      setOpenKeys([]);
    } else if (keys.length === 1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(keys);
    }
  };

  // close the sidebar on small devices after an item is clicked
  const handleItemClick = (key: string) => {
    if (window.innerWidth < 768) {
      toggleSidebar();
    }
  };

  const handleLogout = () => {
    clearToken();
    navigate('/login');
  };

  const showSidebarDataByRole = (item: any) => {
    if (!item.roles) return true;
    if (!currentUser) return true;
    return item.roles.includes(currentUser!.role);
  };

  const findSelectedKey = () => {
    const path = location.pathname;
    for (const item of SidebarData) {
      if (item.path === path) return item.title;
      if (item.path !== '/' && path.startsWith(`${item.path}/`)) return item.title;
      if (item.path === '/events' && path.startsWith('/events/details/')) return item.title;
      if (item.path === '/events' && path.startsWith('/stands/details/')) return item.title;
      if (item.path === '/events' && path.startsWith('/events/') && path.endsWith('/checklist')) return item.title;
      if (item.subNav) {
        for (const sub of item.subNav) {
          if (sub.path === path) return sub.title;
          if (sub.path !== '/' && path.startsWith(`${sub.path}/`)) return sub.title;
        }
      }
    }
    return '';
  };

  const findOpenKey = () => {
    for (const item of SidebarData) {
      if (item.subNav) {
        for (const sub of item.subNav) {
          if (sub.path === location.pathname) return [item.title];
        }
      }
    }
    return [];
  };

  const selectedKey = findSelectedKey();
  const effectiveOpenKeys = openKeys.length ? openKeys : findOpenKey();

  const menuItems = [
    ...SidebarData
      .filter((item) => showSidebarDataByRole(item) && (navigator.onLine ? true : item.showOnOffline))
      .map((item) => ({
        key: item.title,
        icon: item.icon,
        label: item.subNav ? t(item.title) : <Link to={item.path}>{t(item.title)}</Link>,
        children: item.subNav
          ? item.subNav
            .filter((item) => showSidebarDataByRole(item) && (navigator.onLine ? true : item.showOnOffline))
            .map((subItem) => ({
              key: subItem.title,
              icon: subItem.icon,
              label: <Link to={subItem.path}>{t(subItem.title)}</Link>,
            }))
          : undefined,
      })),
  ];

  if (navigator.onLine) {
    menuItems.push({
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('general.logout'),
      style: { marginTop: 'auto' },
      onClick: handleLogout,
    } as any);
  }

  return token ? (
    <Sider
      theme="light"
      className="sidebar"
      collapsible
      collapsed={collapsed}
      onCollapse={toggleSidebar}
      trigger={null}
      width={collapsed ? 80 : 240}
      collapsedWidth={0}
      style={{ borderRight: '1px solid #e8e8e8', height: 'auto' }}
    >
      <div className="logo" />
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={effectiveOpenKeys}
        onOpenChange={handleOpenChange}
        items={menuItems}
        onClick={(e) => handleItemClick(e.key)}
      />
    </Sider>
  ) : (
    <></>
  );
};

export default SidebarComponent;
